package fr.manyverse.app.infrastructure.cache

import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.repository.CharacterCacheRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * In-memory cache for characters.
 * Stores characters in memory for the current table to improve performance.
 */
class CharacterCache : CharacterCacheRepository {
    private val _characters = MutableStateFlow<List<Character>?>(null)
    val characters: StateFlow<List<Character>?> = _characters.asStateFlow()

    /**
     * Checks if the cache has been loaded.
     */
    fun isLoaded(): Boolean = _characters.value != null

    /**
     * Gets the cached characters.
     * Returns null if cache is not loaded.
     */
    override fun getCharacters(): List<Character>? = _characters.value

    /**
     * Sets the entire characters list in the cache.
     */
    override fun setCharacters(characters: List<Character>) {
        _characters.value = characters
    }

    /**
     * Adds or updates a character in the cache.
     * If a character with the same ID exists, it will be replaced.
     */
    override fun upsertCharacter(character: Character) {
        val currentList = _characters.value ?: emptyList()
        val updatedList = currentList.filterNot { it.id == character.id } + character
        _characters.value = updatedList
    }

    /**
     * Removes a character from the cache by ID.
     */
    override fun removeCharacter(characterId: String) {
        val currentList = _characters.value ?: return
        _characters.value = currentList.filterNot { it.id == characterId }
    }

    /**
     * Clears the cache.
     * Should be called on logout.
     */
    override fun clear() {
        _characters.value = null
    }
}
